'use client'

import React from 'react'
import {
  ArrowRight,
  Shield,
  Eye,
  CheckCircle,
  XCircle,
  Layers,
  GitBranch,
  Timer,
  Gauge,
  Building2,
  Rocket,
  AlertTriangle,
  FileCheck,
  Bot,
  User,
  Target,
  Lock,
  Search,
  FileText,
  Users,
  Settings,
  BarChart3,
} from 'lucide-react'

/**
 * Thread-Based Engineering Governance Diagrams
 * Visual components for the Thread-Based Engineering blog post
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

/**
 * Governance Pipeline Comparison Diagram
 * Shows Traditional vs Hive security scanning approaches
 */
export function GovernancePipelineDiagram() {
  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          SECURITY SCANNING: TRADITIONAL VS HIVE
        </h3>
        <p className='text-sm text-muted-foreground'>
          When you catch vulnerabilities determines how much they cost
        </p>
      </div>

      <div className='flex flex-col items-center gap-6'>
        {/* Traditional Pipeline */}
        <div className='w-full'>
          <div className='flex items-center gap-2 mb-3'>
            <XCircle className='w-5 h-5 text-red-500' />
            <span className='text-red-500 font-semibold text-sm uppercase tracking-wide'>
              Traditional Approach
            </span>
          </div>
          <div className='p-4 rounded-xl bg-red-500/10 border border-red-500/30'>
            <div className='flex flex-wrap items-center justify-center gap-2'>
              {[
                { label: 'Generate', icon: Bot },
                { label: 'Review', icon: Eye },
                { label: 'Test', icon: FileCheck },
                { label: 'Scan', icon: Search, highlight: true },
                { label: 'Fix', icon: AlertTriangle, highlight: true },
                { label: 'Deploy', icon: Rocket },
              ].map((step, index, arr) => (
                <React.Fragment key={step.label}>
                  <div
                    className={`flex flex-col items-center p-2 rounded-lg ${step.highlight ? 'bg-red-500/20 border border-red-500/50' : 'bg-muted'}`}
                  >
                    <step.icon
                      className={`w-5 h-5 ${step.highlight ? 'text-red-500' : 'text-muted-foreground'}`}
                    />
                    <span className='text-[10px] text-foreground mt-1'>
                      {step.label}
                    </span>
                  </div>
                  {index < arr.length - 1 && (
                    <ArrowRight className='w-3 h-3 text-muted-foreground flex-shrink-0' />
                  )}
                </React.Fragment>
              ))}
            </div>
            <p className='text-center text-red-500 text-xs mt-3'>
              Vulnerabilities discovered late = expensive remediation cycles
            </p>
          </div>
        </div>

        {/* Hive Pipeline */}
        <div className='w-full'>
          <div className='flex items-center gap-2 mb-3'>
            <CheckCircle className='w-5 h-5 text-green-500' />
            <span className='text-green-600 dark:text-green-400 font-semibold text-sm uppercase tracking-wide'>
              Hive Approach
            </span>
          </div>
          <div className='p-4 rounded-xl bg-green-500/10 border border-green-500/30'>
            <div className='flex flex-wrap items-center justify-center gap-2'>
              {[
                { label: 'Generate + Secure', icon: Shield, highlight: true },
                { label: 'Self-Verify', icon: CheckCircle, highlight: true },
                { label: 'Human Check', icon: User },
                { label: 'Deploy', icon: Rocket },
              ].map((step, index, arr) => (
                <React.Fragment key={step.label}>
                  <div
                    className={`flex flex-col items-center p-2 rounded-lg ${step.highlight ? 'bg-green-500/20 border border-green-500/50' : 'bg-muted'}`}
                  >
                    <step.icon
                      className={`w-5 h-5 ${step.highlight ? 'text-green-600 dark:text-green-400' : 'text-[#FDC304]'}`}
                    />
                    <span className='text-[10px] text-foreground mt-1'>
                      {step.label}
                    </span>
                  </div>
                  {index < arr.length - 1 && (
                    <ArrowRight className='w-3 h-3 text-muted-foreground flex-shrink-0' />
                  )}
                </React.Fragment>
              ))}
            </div>
            <p className='text-center text-green-600 dark:text-green-400 text-xs mt-3'>
              Security embedded at generation = problems prevented, not detected
            </p>
          </div>
        </div>

        {/* Key difference callout */}
        <div className='px-4 py-3 rounded-xl bg-[#008dcc]/10 border border-[#008dcc]/30'>
          <p className='text-xs md:text-sm text-center text-foreground'>
            <span className='font-semibold text-[#008dcc]'>
              Key Difference:
            </span>{' '}
            Hive catches 45% of AI-generated vulnerabilities{' '}
            <span className='text-green-600 dark:text-green-400 font-semibold'>
              before they exist
            </span>
            , not after deployment
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * Four Dimensions of Thread Optimization Grid
 */
export function FourDimensionsGrid() {
  const dimensions = [
    {
      title: 'Run More Threads',
      icon: Layers,
      color: '#008dcc',
      description: 'Parallel execution with independent reviews',
      benefit: 'Prevents cascading failures',
      metric: '10-15 parallel agents',
    },
    {
      title: 'Run Longer Threads',
      icon: Timer,
      color: '#FDC304',
      description: 'Extended autonomy after trust verification',
      benefit: 'Counters 15% success rate',
      metric: 'Earned through metrics',
    },
    {
      title: 'Run Thicker Threads',
      icon: GitBranch,
      color: '#8b5cf6',
      description: 'Sub-agent orchestration with oversight',
      benefit: 'Hierarchical governance',
      metric: 'Singapore MGF aligned',
    },
    {
      title: 'Run Fewer Checkpoints',
      icon: Gauge,
      color: '#10b981',
      description: 'Quality improvement enables autonomy',
      benefit: 'Z-threads when validated',
      metric: 'Trust-based scaling',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          FOUR DIMENSIONS OF THREAD OPTIMIZATION
        </h3>
        <p className='text-sm text-muted-foreground'>
          Scale AI velocity without accumulating technical debt
        </p>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        {dimensions.map(dim => (
          <div
            key={dim.title}
            className='p-4 rounded-xl bg-background border-l-4 border border-border/60'
            style={{ borderLeftColor: dim.color }}
          >
            <div className='flex items-start gap-3'>
              <div
                className='p-2 rounded-lg flex-shrink-0'
                style={{ backgroundColor: `${dim.color}20` }}
              >
                <dim.icon className='w-5 h-5' style={{ color: dim.color }} />
              </div>
              <div className='flex-1'>
                <h4 className='text-foreground font-semibold text-sm mb-1'>
                  {dim.title}
                </h4>
                <p className='text-muted-foreground text-xs mb-2'>
                  {dim.description}
                </p>
                <div className='flex flex-wrap gap-1'>
                  <span
                    className='text-[10px] px-2 py-0.5 rounded-full'
                    style={{
                      backgroundColor: `${dim.color}20`,
                      color: dim.color,
                    }}
                  >
                    {dim.benefit}
                  </span>
                  <span className='text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground'>
                    {dim.metric}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Critical distinction */}
      <div className='mt-4 px-4 py-3 rounded-xl bg-muted border border-border/60'>
        <p className='text-xs md:text-sm text-center text-foreground'>
          <span className='font-semibold'>Critical Distinction:</span> Most
          organizations reduce checkpoints to increase speed. Thread-Based
          Engineering reduces checkpoints by{' '}
          <span className='font-semibold text-[#FDC304]'>
            improving AI output quality first
          </span>
          .
        </p>
      </div>
    </div>
  )
}

/**
 * Implementation Path Timeline - 4 Week Progression
 */
export function ImplementationTimeline() {
  const weeks = [
    {
      week: 1,
      title: 'Base Threads',
      description: 'Verify every result, build trust',
      icon: Eye,
      color: '#008dcc',
      tasks: [
        'Human review on all outputs',
        'Establish baselines',
        'Learn AI patterns',
      ],
    },
    {
      week: 2,
      title: 'Parallel Threads',
      description: 'Add concurrency with verification',
      icon: Layers,
      color: '#FDC304',
      tasks: [
        'Multiple threads simultaneously',
        'Independent reviews',
        'Track quality metrics',
      ],
    },
    {
      week: 3,
      title: 'Test-Driven',
      description: 'Automated verification reduces burden',
      icon: FileCheck,
      color: '#8b5cf6',
      tasks: [
        'Automated quality gates',
        'CI/CD integration',
        'Reduced manual review',
      ],
    },
    {
      week: 4,
      title: 'Long-Duration',
      description: 'L-threads after proven success',
      icon: Timer,
      color: '#10b981',
      tasks: [
        'Extended autonomy earned',
        'Metrics-based trust',
        'First Z-thread candidates',
      ],
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          IMPLEMENTATION PATH: TRUST BUILT INCREMENTALLY
        </h3>
        <p className='text-sm text-muted-foreground'>
          The opposite of "rushing into AI without governance"
        </p>
      </div>

      {/* Timeline */}
      <div className='relative'>
        {/* Connection line - desktop */}
        <div className='hidden md:block absolute top-8 left-[7%] right-[7%] h-1 bg-gradient-to-r from-[#008dcc] via-[#FDC304] via-[#8b5cf6] to-[#10b981] rounded-full' />

        <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
          {weeks.map((week, index) => (
            <div key={week.week} className='relative'>
              {/* Week indicator */}
              <div className='flex flex-col items-center mb-3'>
                <div
                  className='w-10 h-10 rounded-full flex items-center justify-center border-4 border-background z-10'
                  style={{ backgroundColor: week.color }}
                >
                  <week.icon className='w-5 h-5 text-white' />
                </div>
                <span
                  className='text-[10px] font-bold mt-1'
                  style={{ color: week.color }}
                >
                  WEEK {week.week}
                </span>
              </div>

              {/* Content card */}
              <div
                className='bg-background rounded-xl p-3 border-t-2 border border-border/60'
                style={{ borderTopColor: week.color }}
              >
                <h4 className='text-foreground font-semibold text-sm mb-1'>
                  {week.title}
                </h4>
                <p className='text-muted-foreground text-[10px] mb-2'>
                  {week.description}
                </p>
                <ul className='space-y-1'>
                  {week.tasks.map(task => (
                    <li
                      key={task}
                      className='text-[10px] text-muted-foreground flex items-start gap-1'
                    >
                      <CheckCircle
                        className='w-3 h-3 mt-0.5 flex-shrink-0'
                        style={{ color: week.color }}
                      />
                      {task}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Arrow for mobile */}
              {index < weeks.length - 1 && (
                <div className='md:hidden flex justify-center my-2'>
                  <ArrowRight className='w-4 h-4 text-muted-foreground rotate-90' />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Trust trajectory */}
      <div className='mt-6 flex items-center justify-center gap-3 text-xs'>
        <span className='text-muted-foreground'>Trust Level:</span>
        <div className='flex items-center gap-2'>
          <span className='text-red-500'>Low</span>
          <div className='w-24 h-2 rounded-full bg-gradient-to-r from-red-500 via-yellow-500 to-green-500' />
          <span className='text-green-600 dark:text-green-400'>High</span>
        </div>
      </div>
    </div>
  )
}

/**
 * Singapore MGF Compliance Checklist
 */
export function ComplianceChecklist() {
  const requirements = [
    {
      requirement: 'Assess and bound risks upfront',
      hiveImplementation: 'Architecture defines agent limits and permissions',
      icon: Shield,
    },
    {
      requirement: 'Clear allocation of responsibilities',
      hiveImplementation: 'Each agent has defined scope and accountability',
      icon: Users,
    },
    {
      requirement: 'Meaningful human oversight',
      hiveImplementation: 'Human approval at significant checkpoints',
      icon: Eye,
    },
    {
      requirement: 'Automated monitoring',
      hiveImplementation: 'Real-time tracking of agent behavior',
      icon: BarChart3,
    },
    {
      requirement: 'Adaptive governance',
      hiveImplementation: 'System evolves as technology advances',
      icon: Settings,
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='flex items-center justify-center gap-2 mb-2'>
        <Lock className='w-5 h-5 text-[#008dcc]' />
        <span className='text-xl md:text-2xl font-bold text-foreground'>
          SINGAPORE MGF COMPLIANCE
        </span>
      </div>
      <p className='text-sm text-muted-foreground text-center mb-6'>
        World's first governance framework for Agentic AI (January 2026)
      </p>

      <div className='space-y-3'>
        {requirements.map((req, index) => (
          <div
            key={req.requirement}
            className='flex items-start gap-3 bg-background rounded-xl p-3 border border-border/60'
          >
            <div className='flex-shrink-0'>
              <div className='w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center'>
                <CheckCircle className='w-4 h-4 text-green-600 dark:text-green-400' />
              </div>
            </div>
            <div className='flex-1'>
              <div className='flex items-start gap-2'>
                <req.icon className='w-4 h-4 mt-0.5 flex-shrink-0 text-[#008dcc]' />
                <div>
                  <h4 className='text-foreground font-medium text-sm'>
                    {index + 1}. {req.requirement}
                  </h4>
                  <p className='text-muted-foreground text-xs mt-0.5'>
                    <span className='text-[#FDC304] font-medium'>Hive:</span>{' '}
                    {req.hiveImplementation}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Additional frameworks */}
      <div className='mt-4 grid grid-cols-1 md:grid-cols-2 gap-3'>
        <div className='bg-background rounded-xl p-3 border border-border/60'>
          <span className='text-foreground font-semibold text-sm mb-2 flex items-center gap-2'>
            <FileText className='w-4 h-4 text-[#8b5cf6]' />
            WEF Framework (Nov 2025)
          </span>
          <ul className='text-xs text-muted-foreground space-y-1'>
            <li className='flex items-center gap-1'>
              <CheckCircle className='w-3 h-3 text-green-500' />
              Pre-deployment testing
            </li>
            <li className='flex items-center gap-1'>
              <CheckCircle className='w-3 h-3 text-green-500' />
              Clear task boundaries
            </li>
            <li className='flex items-center gap-1'>
              <CheckCircle className='w-3 h-3 text-green-500' />
              Input/output filters
            </li>
          </ul>
        </div>
        <div className='bg-background rounded-xl p-3 border border-border/60'>
          <span className='text-foreground font-semibold text-sm mb-2 flex items-center gap-2'>
            <Shield className='w-4 h-4 text-[#F90B8A]' />
            Enterprise Ready
          </span>
          <ul className='text-xs text-muted-foreground space-y-1'>
            <li className='flex items-center gap-1'>
              <CheckCircle className='w-3 h-3 text-green-500' />
              HIPAA compliance
            </li>
            <li className='flex items-center gap-1'>
              <CheckCircle className='w-3 h-3 text-green-500' />
              SOC 2 audit trails
            </li>
            <li className='flex items-center gap-1'>
              <CheckCircle className='w-3 h-3 text-green-500' />
              Financial services ready
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}

/**
 * Metrics Comparison Diagram
 */
export function MetricsComparisonDiagram() {
  const metrics = [
    {
      metric: 'Code Churn Rate',
      industry: '+41%',
      industryLabel: 'increase',
      tbe: '<10%',
      tbeLabel: 'increase',
      improvement: '75% better',
      icon: Target,
    },
    {
      metric: 'Security Vulnerabilities',
      industry: '45%',
      industryLabel: 'of AI code',
      tbe: '<5%',
      tbeLabel: 'of AI code',
      improvement: '90% reduction',
      icon: Shield,
    },
    {
      metric: 'XSS Failure Rate',
      industry: '86%',
      industryLabel: 'failure',
      tbe: '<15%',
      tbeLabel: 'failure',
      improvement: '83% improvement',
      icon: Lock,
    },
    {
      metric: 'Change Failure Rate',
      industry: 'Rising',
      industryLabel: '',
      tbe: 'Stable',
      tbeLabel: 'or decreasing',
      improvement: 'Controlled',
      icon: BarChart3,
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          INDUSTRY VS THREAD-BASED ENGINEERING
        </h3>
        <p className='text-sm text-muted-foreground'>
          Measurable outcomes from governance-first architecture
        </p>
      </div>

      <div className='space-y-3'>
        {metrics.map(m => (
          <div
            key={m.metric}
            className='bg-background rounded-xl p-4 border border-border/60'
          >
            <div className='flex items-center gap-2 mb-3'>
              <m.icon className='w-4 h-4 text-[#008dcc]' />
              <span className='text-foreground font-semibold text-sm'>
                {m.metric}
              </span>
            </div>

            <div className='grid grid-cols-3 gap-3'>
              {/* Industry */}
              <div className='text-center'>
                <div className='text-[10px] text-muted-foreground uppercase mb-1'>
                  Industry
                </div>
                <div className='text-xl font-bold text-red-500'>
                  {m.industry}
                </div>
                <div className='text-[10px] text-muted-foreground'>
                  {m.industryLabel}
                </div>
              </div>

              {/* Arrow */}
              <div className='flex items-center justify-center'>
                <div className='px-2 py-1 rounded-full text-[10px] font-semibold bg-green-500/20 text-green-600 dark:text-green-400'>
                  {m.improvement}
                </div>
              </div>

              {/* TBE */}
              <div className='text-center'>
                <div className='text-[10px] text-muted-foreground uppercase mb-1'>
                  TBE Target
                </div>
                <div className='text-xl font-bold text-green-600 dark:text-green-400'>
                  {m.tbe}
                </div>
                <div className='text-[10px] text-muted-foreground'>
                  {m.tbeLabel}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Source attribution */}
      <p className='text-center text-muted-foreground text-[10px] mt-4'>
        Sources: GitClear 2025, Stack Overflow 2025, Veracode 2025, DX Research
      </p>
    </div>
  )
}

/**
 * Strategic Positioning - Three Market Segments
 */
export function StrategicPositioningDiagram() {
  const segments = [
    {
      title: 'Companies Facing Crisis',
      subtitle: 'Early Adopters Without Governance',
      icon: AlertTriangle,
      color: '#ef4444',
      stats: ['88% report negative AI impacts', '45% cite "almost right" code'],
      need: 'Remediation expertise',
      pixelmojoValue: 'Frameworks that prevent their mistakes',
    },
    {
      title: 'Conservative Enterprises',
      subtitle: 'Healthcare, Insurance, Finance',
      icon: Building2,
      color: '#008dcc',
      stats: ['Waited for regulatory clarity', 'Need compliance assurance'],
      need: 'Governance-ready AI partner',
      pixelmojoValue: 'Pre-built compliance frameworks',
    },
    {
      title: 'AI-Native Startups',
      subtitle: 'Building AI-First Products',
      icon: Rocket,
      color: '#8b5cf6',
      stats: ['Seen Devin 15% success rate', 'Experienced productivity tax'],
      need: 'Sustainable AI velocity',
      pixelmojoValue: '10-15 parallel agents with quality',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          STRATEGIC MARKET POSITIONING
        </h3>
        <p className='text-sm text-muted-foreground'>
          Three paths to the same destination: governance-first AI development
        </p>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
        {segments.map(segment => (
          <div
            key={segment.title}
            className='bg-background rounded-xl p-4 border-t-4 border border-border/60'
            style={{ borderTopColor: segment.color }}
          >
            <div className='flex items-center gap-2 mb-3'>
              <div
                className='p-2 rounded-lg'
                style={{ backgroundColor: `${segment.color}20` }}
              >
                <segment.icon
                  className='w-5 h-5'
                  style={{ color: segment.color }}
                />
              </div>
              <div>
                <h4 className='text-foreground font-semibold text-sm'>
                  {segment.title}
                </h4>
                <p className='text-muted-foreground text-[10px]'>
                  {segment.subtitle}
                </p>
              </div>
            </div>

            <div className='space-y-3'>
              <div>
                <div className='text-[10px] text-muted-foreground uppercase mb-1'>
                  Situation
                </div>
                <ul className='text-xs text-foreground space-y-1'>
                  {segment.stats.map(stat => (
                    <li key={stat} className='flex items-start gap-1'>
                      <span
                        className='w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0'
                        style={{ backgroundColor: segment.color }}
                      />
                      {stat}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <div className='text-[10px] text-muted-foreground uppercase mb-1'>
                  They Need
                </div>
                <p className='text-xs text-foreground font-medium'>
                  {segment.need}
                </p>
              </div>

              <div
                className='p-2 rounded-lg'
                style={{ backgroundColor: `${segment.color}10` }}
              >
                <div
                  className='text-[10px] uppercase mb-1'
                  style={{ color: segment.color }}
                >
                  Pixelmojo Delivers
                </div>
                <p className='text-xs text-foreground'>
                  {segment.pixelmojoValue}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Bottom line */}
      <div className='mt-4 px-4 py-3 rounded-xl bg-[#FDC304]/10 border border-[#FDC304]/30'>
        <p className='text-xs md:text-sm text-center text-foreground'>
          <span className='font-semibold text-[#FDC304]'>Positioning:</span>{' '}
          Prevention partner, not cleanup crew. Organizations adopting our
          frameworks{' '}
          <span className='text-green-600 dark:text-green-400 font-semibold'>
            avoid
          </span>{' '}
          the 2026-2027 crisis.
        </p>
      </div>
    </div>
  )
}
